import React, { FC, useEffect, useRef } from 'react'
import {
  Form,
  Forms,
  TextInput,
  useFormsContext,
  RadioButton,
  RadioGroup,
  Select,
} from '@ally/forms'
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Label,
  TextBody,
  upToMd,
  useModalContext,
  useSteppedProcess,
} from '@ally/metronome-ui'
import { Private } from '@ally/private'
import allytm from '@ally/allytm'
import { BeneficiaryIdentificationField } from '../BeneficiaryIdentificationField'
import { BeneficiaryAddress } from '../BeneficiaryAddress'
import {
  RELATIONSHIP_OPTIONS,
  SPECIAL_CHAR,
  ZIP_CODE_ERROR,
  RELATIONSHIP_OPTIONS_NO_SPOUSE,
} from '../../../utils/constants'
import { OwnershipTypeField } from '../../NonIraBeneficiaries/AccountOwnership/OwnershipTypeField'
import { BeneficiaryDobField } from '../BeneficiaryDobField'
import { BeneficiaryTypeField } from '../../IraBeneficiaries/AddBeneficiaryFlow/BeneficiaryTypeField'
import { baseValidationSchema } from '../../../utils/yupValidationSchema'
import { Flags } from '../../../utils/feature-flags'
import { useFeatureFlags } from '../../../hooks/use-feature-flags'
import { KeyValuePairObj, fireErrorTagging } from '../../../utils/tagging'

export interface StepTwoFormValues {
  beneficiaryClassification?: string
  beneficiaryName?: string
  firstName?: string
  middleName?: string
  lastName?: string
  dateOfBirth: string
  sameAddress: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  zipCode?: string
  beneIdentification: string
  maskedSsn?: string
  maskedTaxId?: string
  identificationNumber?: string
  issuer?: string
  issueDate?: string
  expirationDate?: string
  whollyOwnedTrust?: string
  ownershipType?: string
  relationshipToOwner?: string
  beneficiaryType?: string
  isMarried: boolean
}

export const stepTwoFormInitialValues = {
  beneficiaryName: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  sameAddress: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
  beneIdentification: '',
  maskedSsn: '',
  maskedTaxId: '',
  identificationNumber: '',
  issuer: '',
  issueDate: '',
  expirationDate: '',
  whollyOwnedTrust: '',
  ownershipType: '',
  relationshipToOwner: '',
  beneficiaryType: '',
  isMarried: false,
}

export const stepTwoBeneErrors: {
  [key: string]: {
    errorMessage: string
    fieldErrorMessage: string
    field: string
  }
} = {
  USB_BUS_351: {
    errorMessage: ZIP_CODE_ERROR,
    fieldErrorMessage: 'Enter correct ZIP code.',
    field: 'zipCode',
  },
  USB_BUS_357: {
    errorMessage: 'Please enter a valid date.',
    fieldErrorMessage: '',
    field: 'dateOfBirth',
  },
  USB_BUS_634: {
    errorMessage: `Account owners can't be beneficiaries.`,
    fieldErrorMessage: 'Enter a valid Social Security Number.',
    field: 'maskedSsn',
  },
  USB_BUS_633: {
    errorMessage: `You can't add the same beneficiary twice. Call us at 1-877-247-2559 if you have questions about how to add your beneficiaries.`,
    fieldErrorMessage: 'Enter a valid Social Security Number.',
    field: 'maskedSsn',
  },
  USB_BUS_350: {
    errorMessage: `Please enter a street address that is not a P.O. Box.`,
    fieldErrorMessage: 'Enter a valid address.',
    field: 'addressLine1',
  },
}

export const StepTwoForm: FC<{
  beneficiaryClassification: string
  isIraPage: boolean
  hasExistingBenes: boolean
  stepTwoFormErrorCode: string
  setStepTwoFormErrorCode: React.Dispatch<React.SetStateAction<string>>
  updateStateStepTwo: (valuesToUpdate: Partial<StepTwoFormValues>) => void
  isMarried?: string
  hasPrimaryBene?: number
}> = ({
  beneficiaryClassification,
  isIraPage,
  hasExistingBenes,
  stepTwoFormErrorCode,
  setStepTwoFormErrorCode,
  updateStateStepTwo,
  isMarried,
  hasPrimaryBene,
}) => {
  const alertRef = useRef<HTMLSpanElement>(null)

  const {
    formId,
    isSubmitting,
    values,
    setFieldValue,
    handleSubmit,
    setFieldTouched,
    setFormError,
    validateForm,
    validateField,
    errors,
  } = useFormsContext()
  const { setOpen } = useModalContext()

  const ffBeneficiariesCommunityPropertyFix = useFeatureFlags(
    Flags.BeneficiariesCommunityPropertyFix,
  )

  const handleChange = (
    name: string,
    value: string | undefined,
    touched?: boolean,
  ): void => {
    setFieldValue(name, value)
    updateStateStepTwo({ [name]: value })
    if (typeof touched !== 'undefined') {
      setFieldTouched(name, touched)
    }
  }

  useEffect(() => {
    if (values.sameAddress === 'Yes') {
      handleChange('addressLine1', '', false)
      handleChange('addressLine2', '', false)
      handleChange('city', '', false)
      handleChange('state', '', false)
      handleChange('zipCode', '', false)
    } else validateForm()
  }, [values.sameAddress])

  const onSubmit = (): void => {
    fireErrorTagging(errors as KeyValuePairObj)
    setFormError('')
    handleSubmit()
  }

  useEffect(() => {
    if (stepTwoFormErrorCode && stepTwoBeneErrors[stepTwoFormErrorCode].field) {
      setFieldTouched(stepTwoBeneErrors[stepTwoFormErrorCode].field, true)
      // TODO - temporary fix for bene alert focus
      setTimeout(() => {
        // istanbul ignore next - low value, focus handling
        alertRef.current?.focus()
      }, 200)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepTwoFormErrorCode])

  useEffect(() => {
    if (!hasExistingBenes && isIraPage) {
      handleChange('beneficiaryType', 'primary')
    }
  }, [])

  const validateWhollyOwnedTrust = (val: string): string => {
    if (!val) {
      return 'Make a selection.'
    }
    // istanbul ignore next // we can remove this ignore once we apply an integration testing for this form
    return ''
  }

  const validateBeneName = (val: string): string => {
    if (!val) {
      return 'Enter a valid beneficiary name.'
    }

    if (!SPECIAL_CHAR.test(val)) {
      return `This field can't contain the following invalid characters: & < > " / # ~ | ^ TAB ,`
    }
    // istanbul ignore next // we can remove this ignore once we apply an integration testing for this form
    return ''
  }

  const validateFirstName = (val: string): string => {
    if (!val) {
      return 'Enter a valid first name.'
    }

    if (!SPECIAL_CHAR.test(val)) {
      return `This field can't contain the following invalid characters: & < > " / # ~ | ^ TAB ,`
    }
    // istanbul ignore next // we can remove this ignore once we apply an integration testing for this form
    return ''
  }

  const validateMiddleName = (val: string): string => {
    if (!val) return ''
    if (!SPECIAL_CHAR.test(val)) {
      return `This field can't contain the following invalid characters: & < > " / # ~ | ^ TAB ,`
    }
    // istanbul ignore next // we can remove this ignore once we apply an integration testing for this form
    return ''
  }

  const validateLastName = (val: string): string => {
    if (!val) {
      return 'Enter a valid last name.'
    }
    if (!SPECIAL_CHAR.test(val)) {
      return `This field can't contain the following invalid characters: & < > " / # ~ | ^ TAB ,`
    }
    // istanbul ignore next // we can remove this ignore once we apply an integration testing for this form
    return ''
  }

  // istanbul ignore next - low value
  const onDateChange = ({
    target: { value },
  }: React.BaseSyntheticEvent): void => {
    // we don't want to allow any digit other than numbers
    const numbersOnly = /[^0-9 //]/g
    if (numbersOnly.test(value)) {
      const updatedValue = value.replace(/[^0-9 //]/g, '')
      handleChange('dateOfBirth', updatedValue)
      // we are adding a backslash automatically as you type
    } else if (value.length === 2 || value.length === 5) {
      const newText = `${value}/`
      handleChange('dateOfBirth', newText)
    } else if (value.length <= 10) {
      handleChange('dateOfBirth', value)
    }
  }

  // adding this logic to avoid user getting stuck when trying to backspace with keyboard
  // istanbul ignore next
  const checkBackspace = (e: React.KeyboardEvent): void => {
    if (
      (e.key === 'Backspace' &&
        values.dateOfBirth[values.dateOfBirth.length - 1] === '/') ||
      e.key === '/'
    ) {
      const newText = values.dateOfBirth.slice(0, -1)
      handleChange('dateOfBirth', newText)
    }
  }

  const showIndividualBeneForm =
    beneficiaryClassification === 'individual' || isIraPage

  // istanbul ignore next - low value
  const onBeneIdentificationChange = ({
    target: { value },
  }: React.BaseSyntheticEvent): void => {
    if (values.identificationNumber) {
      handleChange('identificationNumber', undefined, false)
    }
    if (values.issuer) {
      handleChange('issuer', undefined, false)
    }
    if (values.issueDate) {
      handleChange('issueDate', undefined, false)
    }
    if (values.expirationDate) {
      handleChange('issueDate', undefined, false)
    }
    if (values.maskedSsn || values.maskedTaxId) {
      handleChange('maskedSsn', undefined, false)
      handleChange('maskedTaxId', undefined, false)
    }
    handleChange('beneIdentification', value)
  }

  const handleZipCodeChange = (value: string): void => {
    setStepTwoFormErrorCode('')
    setStepTwoFormErrorCode('')
    handleChange('zipCode', value)
  }

  // istanbul ignore next
  const handleStateChange = (value: string): void => {
    setStepTwoFormErrorCode('')
    setStepTwoFormErrorCode('')
    if (values.zipCode) {
      validateField('zipCode')
    }
    handleChange('state', value)
  }

  const validateIraDropdowns = (val: string): string => {
    if (!val) {
      return 'Make a selection.'
    }
    // istanbul ignore next
    return ''
  }

  // istanbul ignore next - low value
  const relationshipToOwnerOnChange = ({
    target,
  }: React.BaseSyntheticEvent): void => {
    handleChange('relationshipToOwner', target.value)
  }

  // istanbul ignore next - low value
  const onBeneficiaryTypeSelectChange = (e: any): void => {
    handleChange('beneficiaryType', e.target.value)
  }
  // Remove this function once the final flow is decided.
  // istanbul ignore next - This is temporary logic. Once finalize the flow remove the flag and update test case
  const getRelationshipOptions = (): any => {
    if (isMarried === 'No') {
      return RELATIONSHIP_OPTIONS_NO_SPOUSE
    }
    if (
      isMarried === 'Yes' &&
      !ffBeneficiariesCommunityPropertyFix &&
      hasPrimaryBene
    ) {
      return RELATIONSHIP_OPTIONS_NO_SPOUSE
    }
    return RELATIONSHIP_OPTIONS
  }

  // istanbul ignore next - This is temporary logic. Once finalize the flow remove the flag and update test case
  const showOnlySpouse =
    (ffBeneficiariesCommunityPropertyFix && isMarried === 'Yes') ||
    (isMarried === 'Yes' &&
      (hasPrimaryBene === undefined || hasPrimaryBene === 0))
  // istanbul ignore next - This is temporary logic. Once finalize the flow remove the flag and update test case
  const showBeneTypeSelect =
    isMarried !== 'Yes' ||
    (isMarried === 'Yes' &&
      hasPrimaryBene !== undefined &&
      hasPrimaryBene !== 0 &&
      !ffBeneficiariesCommunityPropertyFix)

  return (
    <Form id={formId}>
      {stepTwoFormErrorCode && (
        // istanbul ignore next - Already tested in Step Three
        <Box mb="md">
          <Alert
            variant="error"
            disableContainer
            body={stepTwoBeneErrors[stepTwoFormErrorCode].errorMessage}
            ref={alertRef}
          />
        </Box>
      )}
      {beneficiaryClassification === 'trust' && (
        <Box width={upToMd('88%', '88%')}>
          <Label htmlFor="whollyOwnedTrust">
            Are the grantors of this trust the same as the owners of this Ally
            account?
          </Label>
          <RadioGroup
            name="whollyOwnedTrust"
            legendText=""
            isLegendHidden
            disableDefaultSelection
            validate={validateWhollyOwnedTrust}
            onChange={(selected): void =>
              handleChange('whollyOwnedTrust', selected)
            }
          >
            <RadioButton label="Yes" value="Yes" />
            <RadioButton
              label="No"
              value="No"
              data-testid="whollyOwnedTrustNo"
            />
          </RadioGroup>
        </Box>
      )}
      {beneficiaryClassification !== 'individual' && !isIraPage && (
        <Box width={upToMd('88%', '50%')} my="md">
          <TextInput
            isPrivate
            labelContent="Beneficiary Name"
            name="beneficiaryName"
            inputType="text"
            validate={validateBeneName}
            data-testid="bene-name-input"
            maxLength={40}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
              handleChange('beneficiaryName', e.target.value)
            }
          />
        </Box>
      )}
      {showIndividualBeneForm && (
        <>
          <Box width={upToMd('88%', '50%')} my="md">
            <TextInput
              isPrivate
              labelContent="First Name"
              name="firstName"
              inputType="text"
              validate={validateFirstName}
              data-testid="bene-first-name-input"
              maxLength={20}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                handleChange('firstName', e.target.value)
              }
            />
          </Box>
          <Box width={upToMd('88%', '50%')} mb="md">
            <TextInput
              isPrivate
              labelContent="M.I. (optional)"
              name="middleName"
              inputType="text"
              validate={validateMiddleName}
              maxLength={1}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                handleChange('middleName', e.target.value)
              }
            />
          </Box>
          <Box width={upToMd('88%', '50%')} mb="md">
            <TextInput
              isPrivate
              labelContent="Last Name"
              name="lastName"
              inputType="text"
              validate={validateLastName}
              maxLength={20}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                handleChange('lastName', e.target.value)
              }
            />
          </Box>
          <BeneficiaryDobField
            onDateChange={onDateChange}
            checkBackspace={checkBackspace}
          />
        </>
      )}
      {isIraPage && (
        <Box width={upToMd('88%', '50%')} mb="md">
          {showOnlySpouse && (
            <Box mt="md" mb="sm">
              <Label htmlFor="beneficiaryName">
                Relationship to account owner
              </Label>
              <Private>
                <TextBody tag="span" size="sm">
                  Spouse
                </TextBody>
              </Private>
            </Box>
          )}
          {showBeneTypeSelect && (
            <Private>
              <Select
                name="relationshipToOwner"
                options={getRelationshipOptions()}
                placeholder="Make a selection"
                label="Relationship to account owner"
                validate={validateIraDropdowns}
                onChange={relationshipToOwnerOnChange}
              />
            </Private>
          )}
        </Box>
      )}

      <BeneficiaryIdentificationField
        beneficiaryClassification={beneficiaryClassification}
        changeBeneIdentificationFieldValue={handleChange}
        onBeneIdentificationChange={onBeneIdentificationChange}
        issueDate={values.issueDate}
        expirationDate={values.expirationDate}
        maskedSsn={values.maskedSsn}
        maskedTaxId={values.maskedTaxId}
        identificationNumber={values.identificationNumber}
        beneIdentification={values.beneIdentification}
        setStepTwoFormErrorCode={setStepTwoFormErrorCode}
        isIraPage={isIraPage}
      />
      {(beneficiaryClassification === 'individual' || isIraPage) && (
        <Box width={upToMd('88%', '50%')}>
          <Label htmlFor="sameAddress">Same address as account owner?</Label>
          <RadioGroup
            name="sameAddress"
            legendText=""
            isLegendHidden
            defaultValue="Yes"
            onChange={(selected): void => handleChange('sameAddress', selected)}
          >
            <RadioButton label="Yes" value="Yes" data-testid="sameAddressYes" />
            <RadioButton label="No" value="No" data-testid="sameAddressNo" />
          </RadioGroup>
        </Box>
      )}
      {(showIndividualBeneForm && values.sameAddress === 'No') ||
      (beneficiaryClassification !== 'individual' && !isIraPage) ? (
        <BeneficiaryAddress
          zipCodeError={
            stepTwoFormErrorCode === 'USB_BUS_351'
              ? stepTwoBeneErrors[stepTwoFormErrorCode].fieldErrorMessage
              : ''
          }
          handleZipCodeChange={handleZipCodeChange}
          handleStateChange={handleStateChange}
          handleInputChange={handleChange}
        />
      ) : (
        <></>
      )}
      <BeneficiaryTypeField
        hasExistingBenes={hasExistingBenes}
        isIraPage={isIraPage}
        onBeneficiaryTypeSelectChange={onBeneficiaryTypeSelectChange}
        handleInputChange={handleChange}
        hasPrimaryBene={hasPrimaryBene}
        isMarried={isMarried === 'Yes'}
      />

      {!hasExistingBenes && !isIraPage && (
        <Box width={upToMd('88%', '50%')}>
          <OwnershipTypeField handleOwnershipTypeChange={handleChange} />
        </Box>
      )}

      <Box my="md">
        <ButtonGroup>
          <Button
            allytmln="continue from step two"
            text="Continue"
            disabled={isSubmitting}
            onClick={onSubmit}
            data-testid="submit-continue-button"
          />
          <Button
            allytmln="Cancel Add Beneficiary"
            text="Cancel"
            onClick={
              // istanbul ignore next
              (): void => setOpen(false)
            }
            variant="link"
          />
        </ButtonGroup>
      </Box>
    </Form>
  )
}

export const StepTwoAddNewBene: FC<{
  beneficiaryClassification: string
  isIraPage: boolean
  hasExistingBenes: boolean
  updateStateStepTwo: (valuesToUpdate: Partial<StepTwoFormValues>) => void
  stepTwoFormValues: StepTwoFormValues
  stepTwoFormErrorCode: string
  setStepTwoFormErrorCode: React.Dispatch<React.SetStateAction<string>>
  isMarried?: string
  hasPrimaryBene?: number
}> = ({
  beneficiaryClassification,
  isIraPage,
  hasExistingBenes,
  updateStateStepTwo,
  stepTwoFormValues,
  stepTwoFormErrorCode,
  setStepTwoFormErrorCode,
  isMarried,
  hasPrimaryBene,
}) => {
  const { setNextStepActive } = useSteppedProcess()

  // istanbul ignore next - Low value - Just going to next steps
  const handleSubmit = (): void => {
    setStepTwoFormErrorCode('')
    setNextStepActive()
  }

  const triggerValidationSchema = !(
    stepTwoFormValues.sameAddress === 'Yes' &&
    (beneficiaryClassification === 'individual' || isIraPage)
  )

  useEffect(() => {
    allytm.event(
      'modalView',
      isIraPage
        ? 'AOS:Beneficiaries:Bank IRAs:Add Beneficiary:Beneficiary Information'
        : 'AOS:Beneficiaries:Bank Checking Savings and CDs:Add Beneficiary:Beneficiary Information',
      {
        treatAsPageview: true,
      },
    )
  }, [])

  return (
    <Forms
      formId="beneficiary-classification-form"
      initialValues={stepTwoFormValues}
      onSubmit={handleSubmit}
      validationSchema={triggerValidationSchema && baseValidationSchema}
    >
      <StepTwoForm
        beneficiaryClassification={beneficiaryClassification}
        isIraPage={isIraPage}
        hasExistingBenes={hasExistingBenes}
        stepTwoFormErrorCode={stepTwoFormErrorCode}
        setStepTwoFormErrorCode={setStepTwoFormErrorCode}
        updateStateStepTwo={updateStateStepTwo}
        isMarried={isMarried}
        hasPrimaryBene={hasPrimaryBene}
      />
    </Forms>
  )
}
